import { z } from 'zod';
import { CanonicalWidgetDataSchema } from './dashboard-data.js';

// ─── Widget Data Source ───────────────────────────────────────────────────────

/**
 * Per-widget data source configuration.
 * - `static`: data is authored inline in the builder and stored as CanonicalWidgetData.
 *   Widget components always read from DashboardDataContext — they never inspect `type`.
 * - `agent`: data is fetched at render time via the host app's backend proxy,
 *   which looks up the agent by `agentId`, resolves auth, and fetches
 *   `agent.protocol.url + extensionPath`.
 */
export const WidgetDataSourceSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('static'),
    /** Authored data stored directly in the config — always CanonicalWidgetData. */
    data: CanonicalWidgetDataSchema,
  }),
  z.object({
    type: z.literal('agent'),
    /** FK to the management-api agent record */
    agentId: z.number().int(),
    /** Path appended to the agent's base URL (e.g. "/api/report") */
    extensionPath: z.string(),
    /**
     * Expected response shape for this agent endpoint.
     * Stored at save time (sourced from `getAgentDataExample`) and used by the
     * backend proxy to validate the agent's response before forwarding it to
     * the frontend.
     */
    dataModel: CanonicalWidgetDataSchema,
  }),
]);
export type WidgetDataSource = z.infer<typeof WidgetDataSourceSchema>;

// ─── Widget Types ────────────────────────────────────────────────────────────

export const DashboardWidgetTypeEnum = z.enum(['chart', 'text', 'metric_card', 'code', 'rep_dashboard', 'quote', 'segment_overview']);
export type DashboardWidgetType = z.infer<typeof DashboardWidgetTypeEnum>;

// ─── Chart Widget Config ──────────────────────────────────────────────────────

/** Chart types derived from the QuickChartData discriminated union */
export const DashboardChartTypeEnum = z.enum([
  'bar',
  'stacked-bar',
  'pie',
  'multiple-clustered-bar',
  'multiple-stacked-bar',
  'data-table',
  'kpi-widget',
  'progress-bar-widget',
  'semi-circle-progress-widget',
]);
export type DashboardChartType = z.infer<typeof DashboardChartTypeEnum>;

/** A single row of static chart data entered manually in the builder sidebar */
export const ChartStaticRowSchema = z.object({
  category: z.string(),
  value: z.union([z.string(), z.number()]),
});
export type ChartStaticRow = z.infer<typeof ChartStaticRowSchema>;

/** A named data series for multi-series chart types (clustered/stacked bar) */
export const StaticSeriesSchema = z.object({
  name: z.string(),
  rows: z.array(ChartStaticRowSchema),
});
export type StaticSeries = z.infer<typeof StaticSeriesSchema>;

export const ChartWidgetConfigSchema = z.object({
  title: z.string(),
  chartType: DashboardChartTypeEnum,
  chartThemeId: z.number().int().optional(),
  colors: z.array(z.string()).optional(),
  colorOverrides: z.record(z.string(), z.string()).optional(),
  labelOverrides: z.record(z.string(), z.string()).optional(),
  /** X-axis label shown on the chart */
  xAxisLabel: z.string().optional(),
  /** Y-axis label shown on the chart */
  yAxisLabel: z.string().optional(),
  // Data (staticRows, staticSeries) has moved to WidgetConfig.dataSource.data (CanonicalWidgetData)
});
export type ChartWidgetConfig = z.infer<typeof ChartWidgetConfigSchema>;

// ─── Text Widget Config ───────────────────────────────────────────────────────

export const TextWidgetConfigSchema = z.object({
  title: z.string(),
  /**
   * Display setting: whether to render as rich text (Remirror) or markdown.
   * Defaults to 'richtext' for backwards compatibility.
   * The actual content string lives in WidgetConfig.dataSource.data.
   */
  textMode: z.enum(['richtext', 'markdown']).optional(),
  // content / markdownContent have moved to WidgetConfig.dataSource.data (CanonicalWidgetData)
});
export type TextWidgetConfig = z.infer<typeof TextWidgetConfigSchema>;

// ─── KPI Item ─────────────────────────────────────────────────────────────────

/** A single KPI entry used in metric card widgets */
export const KpiItemSchema = z.object({
  label: z.string(),
  value: z.string(),
  unit: z.string(),
});
export type KpiItem = z.infer<typeof KpiItemSchema>;

// ─── Metric Card Widget Config ────────────────────────────────────────────────

export const MetricCardWidgetConfigSchema = z.object({
  title: z.string(),
  /** Background colour for the metric card (hex string). Defaults to transparent when absent. */
  backgroundColor: z.string().optional(),
  // kpis have moved to WidgetConfig.dataSource.data (CanonicalWidgetData metric_card shape)
});
export type MetricCardWidgetConfig = z.infer<typeof MetricCardWidgetConfigSchema>;

// ─── Code Widget Config ───────────────────────────────────────────────────────

export const CodeWidgetConfigSchema = z.object({
  title: z.string(),
  jsx: z.string(),
  presetType: z.string().optional(),
});
export type CodeWidgetConfig = z.infer<typeof CodeWidgetConfigSchema>;

// ─── Rep Dashboard Widget Config ──────────────────────────────────────────────

export const RepDashboardVisibleSectionsSchema = z.object({
  summary: z.boolean().optional(),
  kpis: z.boolean().optional(),
  opportunities: z.boolean().optional(),
  table: z.boolean().optional(),
});

export const RepDashboardWidgetConfigSchema = z.object({
  title: z.string(),
  /** Which sections to display. All default to true when omitted. */
  visibleSections: RepDashboardVisibleSectionsSchema.optional(),
  /** Background color for collapsible section headers. Defaults to #001845. */
  headerColor: z.string().optional(),
  /** Accent color for values and icon backgrounds. Defaults to #FF5757. */
  accentColor: z.string().optional(),
  /** Default number of table rows per page. Defaults to 5. */
  defaultRowsPerPage: z.number().int().optional(),
});
export type RepDashboardWidgetConfig = z.infer<typeof RepDashboardWidgetConfigSchema>;

// ─── Quote Widget Config ─────────────────────────────────────────────────────

export const QuoteWidgetConfigSchema = z.object({
  title: z.string(),
  /** Optional speaker name displayed below the quote */
  speaker: z.string().optional(),
});
export type QuoteWidgetConfig = z.infer<typeof QuoteWidgetConfigSchema>;

// ─── Widget Config (discriminated union) ─────────────────────────────────────

/** Optional source attribution metadata for any widget */
export const SourceAttributionSchema = z.object({
  /** Human-readable label, e.g. "Claims Data Q1-Q4 2024" */
  label: z.string(),
  /** Source type, e.g. "file", "api", "ai-generated" */
  type: z.string().optional(),
}).optional();

export const WidgetConfigSchema = z.discriminatedUnion('type', [
  z.object({ id: z.string(), type: z.literal('chart'), config: ChartWidgetConfigSchema, dataSource: WidgetDataSourceSchema.optional(), sourceAttribution: SourceAttributionSchema }),
  z.object({ id: z.string(), type: z.literal('text'), config: TextWidgetConfigSchema, dataSource: WidgetDataSourceSchema.optional(), sourceAttribution: SourceAttributionSchema }),
  z.object({ id: z.string(), type: z.literal('metric_card'), config: MetricCardWidgetConfigSchema, dataSource: WidgetDataSourceSchema.optional(), sourceAttribution: SourceAttributionSchema }),
  z.object({ id: z.string(), type: z.literal('code'), config: CodeWidgetConfigSchema, dataSource: WidgetDataSourceSchema.optional(), sourceAttribution: SourceAttributionSchema }),
  z.object({ id: z.string(), type: z.literal('rep_dashboard'), config: RepDashboardWidgetConfigSchema, dataSource: WidgetDataSourceSchema.optional(), sourceAttribution: SourceAttributionSchema }),
  z.object({ id: z.string(), type: z.literal('quote'), config: QuoteWidgetConfigSchema, dataSource: WidgetDataSourceSchema.optional(), sourceAttribution: SourceAttributionSchema }),
  z.object({ id: z.string(), type: z.literal('segment_overview'), config: z.object({ title: z.string().optional() }), dataSource: WidgetDataSourceSchema.optional(), sourceAttribution: SourceAttributionSchema }),
]);
export type WidgetConfig = z.infer<typeof WidgetConfigSchema>;

// ─── WidgetSpecificConfig union ─────────────────────────────────────────────

/**
 * Union of every widget-specific config type.
 * Use this as the constraint for generic `TConfig` parameters (e.g. in
 * `WidgetRegistryEntry<TConfig>`) so callers are restricted to real widget
 * configs rather than arbitrary objects.
 */
export type WidgetSpecificConfig =
  | ChartWidgetConfig
  | TextWidgetConfig
  | MetricCardWidgetConfig
  | CodeWidgetConfig
  | RepDashboardWidgetConfig
  | QuoteWidgetConfig;

// ─── Block ────────────────────────────────────────────────────────────────────

/**
 * A block is a container for one optional widget.
 * Blocks are ordered in an array; x/y are derived at render time by row-packing.
 * Height is always 1 row; width (w) is 1, 2, or 3 columns.
 */
/** A slot/column position within a block (1 = left, 2 = centre, 3 = right) */
export type Slot = 1 | 2 | 3;

/** A widget placed at a specific slot inside a block or section, with a column span */
export const BlockWidgetSchema = z.object({
  /** 1-indexed row within the block (row 1 = first row). Defaults to 1 for legacy data. */
  row: z.number().int().min(1).default(1),
  slot: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  span: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  widget: WidgetConfigSchema,
});
export type BlockWidget = z.infer<typeof BlockWidgetSchema>;

// ─── Section ──────────────────────────────────────────────────────────────────

/**
 * A section is a sub-block that lives at a (row, slot, span) position inside a
 * parent block. It has its own independent 3-column widget grid (same row/slot/span
 * system as a block). Sections cannot hold other sections — only widgets.
 */
export const SectionConfigSchema = z.object({
  id: z.string(),
  title: z.string(),
  /** When true, the section title is hidden in the rendered dashboard (compact strip style in builder). */
  hideTitle: z.boolean().optional(),
  /** When true, the section can be collapsed/expanded in the rendered dashboard (accordion-style). */
  collapsible: z.boolean().optional(),
  /** When true AND collapsible, the section starts collapsed (user can expand). */
  defaultCollapsed: z.boolean().optional(),
  /** Explicit minimum inner row count set by the drag-resize handle. Auto-grows from content when not set. */
  minInnerRows: z.number().int().min(1).optional(),
  /** Widgets placed inside this section */
  widgets: z.array(BlockWidgetSchema).default([]),
  /** Opaque metadata (e.g. tag info) preserved across saves. */
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type SectionConfig = z.infer<typeof SectionConfigSchema>;

// ─── Block Items (discriminated union) ───────────────────────────────────────

/** A widget item placed directly inside a block */
export const BlockWidgetItemSchema = z.object({
  type: z.literal('widget'),
  row: z.number().int().min(1).default(1),
  slot: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  span: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  /**
   * Number of block rows this widget occupies vertically.
   * Default 1. Higher values make the widget taller, snapping to row-height units.
   */
  rowSpan: z.number().int().min(1).optional(),
  widget: WidgetConfigSchema,
});
export type BlockWidgetItem = z.infer<typeof BlockWidgetItemSchema>;

/** A section item placed directly inside a block */
export const BlockSectionItemSchema = z.object({
  type: z.literal('section'),
  row: z.number().int().min(1).default(1),
  slot: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  span: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  section: SectionConfigSchema,
});
export type BlockSectionItem = z.infer<typeof BlockSectionItemSchema>;

/** A discriminated union of everything that can be placed at a (row, slot) inside a block */
export const BlockItemSchema = z.discriminatedUnion('type', [
  BlockWidgetItemSchema,
  BlockSectionItemSchema,
]);
export type BlockItem = z.infer<typeof BlockItemSchema>;

export const BlockConfigSchema = z.object({
  id: z.string(),
  title: z.string(),
  /** When true, the block title is hidden in the rendered dashboard (shows in section-title style in the builder) */
  hideTitle: z.boolean().optional(),
  /** Number of grid columns the block spans: 1 (narrow), 2 (half), or 3 (full row) */
  w: z.union([z.literal(1), z.literal(2), z.literal(3)]).default(3),
  /**
   * Starting column of the block in the 3-column grid (0 = left, 1 = centre, 2 = right).
   * Persisted after the user drags a block horizontally. Defaults to 0 when absent.
   */
  x: z.number().int().min(0).max(2).optional(),
  /**
   * Minimum number of rows shown (user-controlled block height).
   * The actual row count is max(minRows, derived-from-items).
   */
  minRows: z.number().int().min(1).optional(),
  /** When true, the block can be collapsed/expanded in the rendered dashboard (accordion-style). */
  collapsible: z.boolean().optional(),
  /** When true AND collapsible, the block starts collapsed (user can expand). */
  defaultCollapsed: z.boolean().optional(),
  /** Items (widgets or sections) placed in the block's column slots */
  items: z.array(BlockItemSchema).default([]),
});
export type BlockConfig = z.infer<typeof BlockConfigSchema>;

// ─── Custom Theme ─────────────────────────────────────────────────────────────

/**
 * A user-defined named colour palette stored directly in the dashboard config.
 * IDs are assigned by the client (Date.now()) and are scoped to the page — they
 * never collide with DB-assigned branding palette IDs.
 */
export const CustomThemeSchema = z.object({
  /** Client-assigned integer ID (e.g. Date.now()). Unique within a DashboardConfig. */
  id: z.number().int(),
  name: z.string(),
  /** Ordered hex colour strings, between 5 and 25 entries. */
  colors: z.array(z.string()).min(5).max(25),
});
export type CustomTheme = z.infer<typeof CustomThemeSchema>;

// ─── Dashboard Config (root) ──────────────────────────────────────────────────

export const DashboardConfigSchema = z.object({
  /** When true, the dashboard title header is hidden in the rendered view (compact in builder). */
  hideTitle: z.boolean().optional(),
  /** Ordered list of blocks. y positions are derived from column-height packing; x is persisted per block. */
  blocks: z.array(BlockConfigSchema),
  /**
   * User-defined named colour palettes for this page. Stored inline so they
   * persist automatically with every page version save. Selected via
   * ChartWidgetConfig.chartThemeId (matched by id).
   */
  customThemes: z.array(CustomThemeSchema).optional(),
});
export type DashboardConfig = z.infer<typeof DashboardConfigSchema>;


